import React, { useContext, useCallback, useMemo } from "react";
import styled from "styled-components";
import { makeArrayFromLength, getText, UI_BODY_SM_DF, UI_TAG, Divider, colors2 } from "oolib";
import { Lightbox, LightboxTrigger, LightboxContent, LightboxContext } from "@/components/UI/lightbox";
import { ListItem } from "@/components/UI/List/comps/ListItem";
import { useDragSelect } from "@/components/UI/List/hooks/useDragSelect";
import { DrilldownDepthContext } from "../context/DrilldownDepthContext";
import { globalScrollbarStyling } from "oolib/dist/v2/themes/globalStyles";

const StyledLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

export const DrilldownLevel = ({
  options = [],
  value = [],
  isMulti = true,
  isLoading = false,
  isFetchingNextPage = false,
  observerRef = null,
  onChange,
  onDrilldown,
  getChildLevel,
  folderSelectionMode = "exclusive",
  baseZIndex = 100000,
  listStyle,
  isRoot = false,
  emptyMessage,
  headerContent,
  showSelectedFirst = false,
}) => {
  const parentDepth = useContext(DrilldownDepthContext);
  const zIndex = baseZIndex + parentDepth;
  const lightboxCtx = useContext(LightboxContext);
  const handleClose = useCallback(() => lightboxCtx?.setOpen(false), [lightboxCtx]);

  // Inject onClose into headerContent for non-root levels
  const resolvedHeaderContent = !isRoot && headerContent && React.isValidElement(headerContent)
    ? React.cloneElement(headerContent, { onClose: handleClose })
    : headerContent;

  // Bridge: useDragSelect expects parentOnChange(id, option), new API has onChange(option)
  const wrappedOnChange = useCallback(
    (_id, option) => onChange?.(option),
    [onChange]
  );

  const {
    optimisticSelections,
    handleDragStart,
    handleDragOver,
    handleItemChange,
  } = useDragSelect({
    options,
    value,
    id: "level",
    isMulti,
    isTagsStyle: false,
    dragSelectAcrossGroups: false,
    parentOnChange: wrappedOnChange,
  });

  const handleDrilldownOpen = useCallback(
    (option, isOpen) => {
      if (!isOpen) return;
      onDrilldown?.(option);
    },
    [onDrilldown]
  );

  const SKELETON_COUNT = 1;
  const skeletonOptions = useMemo(
    () => makeArrayFromLength(SKELETON_COUNT).map(() => ({ loading: true })),
    []
  );

  const displayOptions = isLoading
    ? skeletonOptions
    : isFetchingNextPage
      ? [...options, ...skeletonOptions]
      : options;

  if (!isLoading && displayOptions.length === 0) {
    const emptyContent = (
      <div style={{ padding: "1.2rem 0.8rem" }}>
        <UI_BODY_SM_DF style={{ fontStyle: "italic", color: colors2.grey50 }}>
          {emptyMessage || "No options available"}
        </UI_BODY_SM_DF>
      </div>
    );

    if (isRoot && !headerContent) return emptyContent;
    if (isRoot) return (
      <StyledLevelWrapper>
        {resolvedHeaderContent}
        <StyledWrapper style={listStyle}>{emptyContent}</StyledWrapper>
      </StyledLevelWrapper>
    );

    return (
      <StyledLevelWrapper>
        {resolvedHeaderContent}
        <StyledWrapper
          id="drilldownLevel"
          style={listStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {emptyContent}
        </StyledWrapper>
      </StyledLevelWrapper>
    );
  }

  // showSelectedFirst: compute boundary for visual split (callers already sort selected first)
  const _selIds = showSelectedFirst ? new Set(value?.map(v => v.value) || []) : null;
  const _firstUnselIdx = showSelectedFirst && _selIds?.size
    ? displayOptions.findIndex(o => !o.loading && !_selIds.has(o._id ?? o.value))
    : -1;
  const _hasVisualSplit = showSelectedFirst && _selIds?.size > 0 && _firstUnselIdx !== 0 && !isLoading;

  const listContent = (
    <StyledListContent
      id="listcontent"
      style={{
        listStyle: "none",
        display: "flex",
        flexDirection: "column",
        ...(isRoot ? { gap: "0.2rem" } : {}),
      }}
    >
      {(() => {
        const items = displayOptions.map((option, index) => {
          if (option.loading) {
            return <ListItem key={`skeleton-${index}`} option={option} isMulti={isMulti} />;
          }

          const canDrilldown = option.hasChildren && !!getChildLevel;
          const isSelected = value?.some(
            (v) => v.value === (option._id ?? option.value)
          );
          const isLastRealItem = index === options.length - 1;

          if (canDrilldown) {
            const childLevel = getChildLevel(option);
            const drilldownEnabled =
              folderSelectionMode === "exclusive" ? !isSelected : true;

            return (
              <DrilldownDepthContext.Provider
                value={parentDepth + 1}
                key={option._id ?? option.value}
              >
                <Lightbox
                  placement="right-start"
                  onOpenChange={(open) =>
                    handleDrilldownOpen(option, open)
                  }
                  enabled={drilldownEnabled}
                  open={
                    folderSelectionMode === "exclusive" && isSelected
                      ? false
                      : undefined
                  }
                >
                  <LightboxTrigger>
                    <ListItem
                      option={option}
                      isSelected={isSelected}
                      isMulti={isMulti}
                      value={value}
                      S={true}
                      index={index}
                      observerRef={isLastRealItem ? observerRef : null}
                      onChange={handleItemChange}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      optimisticSelections={optimisticSelections}
                      hasSubmenu
                      showSubmenuCaret={drilldownEnabled}
                      style={{ width: "100%" }}
                    />
                  </LightboxTrigger>
                  <LightboxContent
                    cascade
                    // title={
                    //   getText(option.display) ??
                    //   getText(option?.main?.title) ??
                    //   ""
                    // }
                    style={{ zIndex }}
                  >
                    {childLevel && (
                      <DrilldownLevel
                        {...childLevel}
                        folderSelectionMode={folderSelectionMode}
                        baseZIndex={baseZIndex}
                        listStyle={listStyle}
                      />
                    )}
                  </LightboxContent>
                </Lightbox>
              </DrilldownDepthContext.Provider>
            );
          }

          // Leaf option — hasSubmenu is false by default, so chevron/active-bg
          // don't leak from any ambient cascade Lightbox.
          return (
            <ListItem
              key={option._id ?? option.value}
              option={option}
              isMulti={isMulti}
              value={value}
              index={index}
              // Single-select has no checkbox, so surface the current selection as
              // a highlighted row. Gated on !isMulti so multi-select (the V3 filter,
              // which shows selection via its checkbox) is unaffected.
              isSelected={!isMulti && value?.some((v) => v.value === (option._id ?? option.value))}
              observerRef={isLastRealItem ? observerRef : null}
              onChange={handleItemChange}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              optimisticSelections={optimisticSelections}
            />
          );
        });
        if (!_hasVisualSplit) return items;
        const result = [
          <UI_TAG key="__sel-header" style={{ padding: "0.6rem 0.8rem 0.4rem", color: colors2.grey50 }}>Selected</UI_TAG>
        ];
        for (let i = 0; i < items.length; i++) {
          if (i === _firstUnselIdx) {
            result.push(<div key="__sel-divider" style={{ margin: "0.4rem 0" }}><Divider /></div>);
          }
          result.push(items[i]);
        }
        return result;
      })()}
    </StyledListContent>
  );

  // Root level: bare list unless headerContent exists (needs scroll separation)
  if (isRoot && !headerContent) {
    return listContent;
  }
  if (isRoot) {
    return (
      <StyledLevelWrapper>
        {resolvedHeaderContent}
        <StyledWrapper style={listStyle} onClick={(e) => e.stopPropagation()}>
          {listContent}
        </StyledWrapper>
      </StyledLevelWrapper>
    );
  }

  return (
    <StyledLevelWrapper>
      {resolvedHeaderContent}
      <StyledWrapper
        id="drilldownLevel"
        style={listStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {listContent}
      </StyledWrapper>
    </StyledLevelWrapper>
  );
};


export const StyledWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  margin-top: 0.4rem;
  ${globalScrollbarStyling}
`

export const StyledListContent = styled.div`
  
  /* ${globalScrollbarStyling} */
`